import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INVITATION_KEY = 'cricket_invitation_token';

/**
 * /i/:token — coach SMS invitation landing.
 *
 * Shows who's inviting + the club name + the optional note, then
 * either:
 *   - if not signed in: stashes the token in localStorage and routes
 *     to /onboarding (which will forward it on /onboarding/complete).
 *   - if already signed in: calls /coach/roster/claim immediately —
 *     handles the case where a parent already has an account.
 */
export default function InviteAcceptPage() {
  const { token: inviteToken } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { token: authToken, identity } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    axios.get(`${API}/coach/roster/preview/${inviteToken}`)
      .then((r) => {
        setPreview(r.data);
        if (r.data?.status === 'expired') setError('This invite expired.');
        if (r.data?.status === 'consumed') setError('This invite has already been used.');
        if (r.data?.status === 'revoked') setError('Your coach revoked this invite.');
      })
      .catch((err) => {
        setError(err.response?.status === 404
          ? 'Invite not found — check the link.'
          : 'Could not load invite.');
      });
  }, [inviteToken]);

  const proceed = async () => {
    if (!inviteToken) return;
    if (!authToken) {
      // New user path: stash + send to onboarding.
      localStorage.setItem(INVITATION_KEY, inviteToken);
      navigate('/onboarding', { replace: true });
      return;
    }
    // Already authenticated: claim immediately (creating a household if needed).
    setClaiming(true);
    try {
      // If they don't have a household yet, we don't have an ID to send.
      // In that case just stash the token and send them to onboarding to
      // finish setup — onboarding/complete auto-claims.
      if (!identity?.household_id) {
        localStorage.setItem(INVITATION_KEY, inviteToken);
        navigate('/onboarding', { replace: true });
        return;
      }
      const res = await axios.post(`${API}/coach/roster/claim`, {
        token: inviteToken,
        household_id: identity.household_id,
      }, { headers: { Authorization: `Bearer ${authToken}` } });
      if (res.data?.ok) {
        setClaimed(true);
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      } else {
        setError(res.data?.error || 'Could not link coach.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not link coach.');
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
      style={{
        background: theme === 'dark'
          ? 'radial-gradient(circle at 20% 0%, #0e2438, #060818 60%)'
          : 'radial-gradient(circle at 20% 0%, #e9fbf3, #f8fafc 60%)',
        color: theme === 'dark' ? '#f7fafc' : '#0f172a',
      }}
    >
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size={40} /></div>

        <div
          className="rounded-3xl p-7"
          style={{
            background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(0,201,167,0.2)',
            backdropFilter: 'blur(12px)',
          }}
        >
          {error ? (
            <>
              <div className="text-3xl mb-1">😕</div>
              <h1 className="text-2xl font-extrabold tracking-tight">Hmm</h1>
              <p className="text-sm opacity-70 mt-2">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="w-full mt-6 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: 'rgba(255,255,255,0.08)' }}
              >
                Go home
              </button>
            </>
          ) : claimed ? (
            <>
              <div className="text-3xl mb-1">✅</div>
              <h1 className="text-2xl font-extrabold tracking-tight">You're in</h1>
              <p className="text-sm opacity-70 mt-2">
                {preview?.coach_display_name || 'Your coach'} can now see your
                athlete's shared sessions. Taking you to the dashboard…
              </p>
            </>
          ) : !preview ? (
            <p className="opacity-70">Loading invite…</p>
          ) : (
            <>
              <div
                className="text-[10px] uppercase tracking-widest font-bold mb-2"
                style={{ color: '#00C9A7' }}
              >
                ✨ You're invited
              </div>
              <h1 className="text-2xl font-extrabold tracking-tight">
                {preview.coach_display_name || 'Your coach'}
                <span className="block text-base font-normal opacity-70 mt-1">
                  invites you to Cricket Refreshed
                  {preview.club_name && <> · {preview.club_name}</>}
                </span>
              </h1>

              {preview.athlete_name_hint && (
                <p className="text-sm opacity-90 mt-3">
                  For <strong>{preview.athlete_name_hint}</strong>.
                </p>
              )}
              {preview.note && (
                <div
                  className="mt-4 px-3 py-3 rounded-lg text-sm"
                  style={{
                    background: 'rgba(0,201,167,0.08)',
                    border: '1px solid rgba(0,201,167,0.2)',
                  }}
                >
                  "{preview.note}"
                </div>
              )}

              <ul className="text-sm opacity-80 mt-5 space-y-1.5">
                <li>· AI Coach Twin chats — anytime</li>
                <li>· Video upload + pose-overlay feedback</li>
                <li>· 7-day adaptive practice plan</li>
                <li>· Free to start, 50 credits/month</li>
                <li className="font-semibold" style={{ color: '#00C9A7' }}>
                  · +50 bonus credits on signup (this invite only)
                </li>
              </ul>

              <button
                onClick={proceed}
                disabled={claiming}
                className="w-full mt-6 py-3 rounded-xl font-semibold text-white"
                style={{
                  background: claiming
                    ? 'rgba(150,160,180,0.3)'
                    : 'linear-gradient(135deg, #00A651, #00D9FF)',
                }}
              >
                {claiming ? 'Linking your coach…' : 'Get started'}
              </button>

              <p className="text-[11px] opacity-50 mt-3 text-center">
                Your coach can only see sessions you choose to share.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export const INVITATION_TOKEN_KEY = INVITATION_KEY;
